import React, {useEffect, useMemo, useState, useRef} from 'react';
import './App.css';
import ItemDisplay from './components/ItemDisplay';
import RestaurantMap from './components/RestaurantMap';
import {MenuItem, menuItems as fallbackItems} from "./constants/menuItems";
import {parseCSV} from './utils/parseCsv';
import {getFoodImageUrl} from './utils/images';

const currentYear: number = new Date().getFullYear();

// Type definitions
interface SpecialDateRange {
  startDate: string;
  endDate: string;
  status: string;
}

interface HoursConfig {
  regular: {
    [key: string]: string;
  };
  specialRanges: SpecialDateRange[];
}

interface DateHours {
  date: string;
  dayLabel: string;
  hours: string;
  isToday?: boolean;
}

// HOURS CONFIGURATION - Easy to customize
const RESTAURANT_HOURS: HoursConfig = {
  // Regular weekly hours
  regular: {
    sunday: '11:00 AM - 9:00 PM',
    monday: 'Closed',
    tuesday: '11:00 AM - 9:00 PM',
    wednesday: '11:00 AM - 9:00 PM',
    thursday: '11:00 AM - 9:00 PM',
    friday: '11:00 AM - 9:00 PM',
    saturday: '11:00 AM - 9:00 PM',
  },
  // Special date ranges (format: 'YYYY-MM-DD')
  // Example: restaurant closed Jan 2-20, 2025
  specialRanges: [
    { startDate: '2026-01-02', endDate: '2026-01-20', status: 'Closed' },
    // Holiday Hours 2025-2026
    // { startDate: '2024-12-24', endDate: '2024-12-25', status: 'Closed - Christmas' },
    // { startDate: '2024-12-31', endDate: '2025-01-01', status: 'Closed - New Year' },
    // { startDate: '2025-12-24', endDate: '2025-12-25', status: 'Closed - Christmas' },
    // { startDate: '2025-12-31', endDate: '2026-01-01', status: 'Closed - New Year' },
    // Reduced holiday hours
    // { startDate: `${currentYear}-10-31`, endDate: `${currentYear}-10-31`, status: '4:00 PM - 9:00 PM - Halloween' },
  ],
};


// Generate constant weekly hours display (Sunday to Saturday) with current date numbers
const getConstantWeeklyHours = (): DateHours[] => {
  const daysOfWeek: string[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const dayNames: string[] = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const today = new Date();
  
  // Find the current day of week (0=Sunday, 1=Monday, etc.)
  const currentDayIndex = today.getDay();
  
  // Helper function to format date as YYYY-MM-DD in local timezone
  const formatDateLocal = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  const todayDateStr = formatDateLocal(today);
  
  return daysOfWeek.map((day: string, index: number) => {
    // Calculate the date for this day of the current week
    const date = new Date(today);
    const daysToAdd = index - currentDayIndex;
    date.setDate(today.getDate() + daysToAdd);
    
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const dayNumber = String(date.getDate()).padStart(2, '0');
    const dateStr = formatDateLocal(date);
    
    // Check if this is today
    const isToday = dateStr === todayDateStr;
    const dayLabel = `${month}/${dayNumber} (${dayNames[index].substring(0, 3)})`;
    
    // Check if date falls in any special range
    let hours = RESTAURANT_HOURS.regular[day] || '11:00 AM - 9:00 PM';
    for (const range of RESTAURANT_HOURS.specialRanges) {
      if (dateStr >= range.startDate && dateStr <= range.endDate) {
        hours = range.status;
        break;
      }
    }
    
    return {
      date: day,
      dayLabel: dayLabel,
      hours: hours,
      isToday: isToday,
    };
  });
};

const App: React.FC = () => {
  const [items, setItems] = useState<MenuItem[]>(fallbackItems);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const categoryScrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetch('/menu.csv')
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch');
        return res.text();
      })
      .then(text => {
        const parsed = parseCSV(text);
        if (parsed.length > 0) {
          const menuItems: MenuItem[] = parsed.map(row => ({
            itemName: row.itemName || row.name || '',
            categories: row.categories || row.category || '',
            description: row.description || '',
            smallPrice: row.small_price || row.smallPrice || '',
            regularPrice: row.regular_price || row.regularPrice || '',
            largePrice: row.large_price || row.largePrice || ''
          }));
          setItems(menuItems);
        }
      })
      .catch(() => {
        // keep fallback
      });
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.pageYOffset > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const categories = useMemo(() => {
    const s = new Set<string>();
    s.add('All');
    items.forEach(it => {
      if (it.categories) {
        it.categories.split(';').map(c => c.trim()).filter(Boolean).forEach(c => s.add(c));
      }
    });
    return Array.from(s);
  }, [items]);

  const groupedItems = useMemo(() => {
    const groups: { [key: string]: MenuItem[] } = {};
    
    items.forEach(item => {
      const itemCategories = item.categories ? item.categories.split(';').map(c => c.trim()).filter(Boolean) : ['Other'];
      itemCategories.forEach(cat => {
        if (!groups[cat]) {
          groups[cat] = [];
        }
        groups[cat].push(item);
      });
    });
    
    return groups;
  }, [items]);

  const scrollToCategory = (category: string) => {
    if (category === 'All') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    const element = document.getElementById(`category-${category}`);
    if (element) {
      const offset = 150; // Account for sticky header
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollCategories = (direction: 'left' | 'right') => {
    if (categoryScrollRef.current) {
      const scrollAmount = categoryScrollRef.current.offsetWidth * 0.8; // 80% of visible width
      const newScrollLeft = categoryScrollRef.current.scrollLeft + 
        (direction === 'left' ? -scrollAmount : scrollAmount);
      categoryScrollRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  const sevenDaysHours = getConstantWeeklyHours();

  return (
    <div className='app-container'>
      <div className="site-header">
        <header className="header-content">
          <div>
            <h3 className="header-title">Welcome to Wong's Garden Restaurant</h3>
          </div>
        </header>
      </div>

      <div className="restaurant-info">
        <div className="info-content">
          {/* <h2>Welcome to Wong's Garden</h2> */}
          {/* <p className="info-tagline">Authentic Chinese Cuisine Since 2005</p> */}
          <div className="info-details">
            <div className="info-item">
              <span className="info-icon">📍</span>
              <div>
                <strong>Location</strong>
                <p>263 Weller Ave #3, Kingston, ON K7K 5B5</p><RestaurantMap compact={true} height="150px" />
              </div>
            </div>
            <div className="info-item">
              <span className="info-icon">⏰</span>
              <div>
                <strong>Hours</strong>
                <div className="hours-list">
                  {sevenDaysHours.map((day) => (
                    <div key={day.date} className="hours-row">
                      <span className="day">
                        {day.isToday ? <strong>{day.dayLabel}</strong> : day.dayLabel}
                      </span>
                      <span className="time">
                        {day.isToday ? <strong>{day.hours}</strong> : day.hours}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="info-item">
              <span className="info-icon">📞</span>
              <div>
                <strong>Contact</strong>
                <p>(613) 542-4709</p>
              </div>
            </div>
          </div>
          {/* <p className="info-description">
            Experience the finest Chinese cuisine in town. From traditional favorites to modern interpretations, 
            our chefs prepare each dish with authentic ingredients and time-honored techniques. 
            Takeout and delivery available.
          </p> */}
        </div>
      </div>


      <div className='menu-items-container'>
        <div className="search-filter-container">
          <button 
            className="category-scroll-btn category-scroll-left"
            onClick={() => scrollCategories('left')}
            aria-label="Scroll categories left"
          >
            ‹
          </button>
          <div className="category-buttons" ref={categoryScrollRef}>
            {categories.map(c => (
              <button
                key={c}
                className="category-btn"
                onClick={() => scrollToCategory(c)}
              >
                {c}
              </button>
            ))}
          </div>
          <button 
            className="category-scroll-btn category-scroll-right"
            onClick={() => scrollCategories('right')}
            aria-label="Scroll categories right"
          >
            ›
          </button>
        </div>

        {Object.keys(groupedItems).length > 0 ? (
          <div className="categories-container">
            {categories.filter(c => c !== 'All' && groupedItems[c]).map(category => (
              <div key={category} className="category-section">
                <h2 id={`category-${category}`} className="category-header">
                  {category}
                </h2>
                <div className="items-grid">
                  {groupedItems[category].map((item: MenuItem, i) => (
                    <ItemDisplay
                      key={item.itemName + i}
                      itemName={item.itemName}
                      imageSrc={[getFoodImageUrl(item.itemName, category)]}
                      description={item.description || ''}
                      smallPrice={item.smallPrice || ''}
                      regularPrice={item.regularPrice || ''}
                      largePrice={item.largePrice || ''}
                      isCombo={category === 'Family Dinners'}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="no-items">
            <p>No menu items available</p>
          </div>
        )}
      </div>

      <footer className="site-footer">
        <div className="footer-content">
          <span className="footer-text">© {currentYear} Wong's Garden Restaurant. All rights reserved.</span>
        </div>
      </footer>

      {showScrollTop && (
        <button
          className="scroll-to-top"
          onClick={scrollToTop}
          aria-label="Scroll to top"
        >
          ↑
        </button>
      )}
    </div>
  );
};

export default App;
